import type { ConsultationLead } from '../types/index.ts';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';

export interface SubmitLeadResponse {
  success: boolean;
  isPreview?: boolean;
  leadId?: string;
  message?: string;
  error?: string;
  errors?: Record<string, string>;
}

const stagingFirebaseConfig = {
  projectId: 'everforge-fitness-staging',
  appId: '1:48697165878:web:ef990a1b5fea9945bcb097',
  storageBucket: 'everforge-fitness-staging.firebasestorage.app',
  apiKey: 'AIzaSyB2NeLA_O8tQM2n9CLCCR5So4NLTcd7pTU',
  authDomain: 'everforge-fitness-staging.firebaseapp.com',
  messagingSenderId: '48697165878',
};

function getStagingDb() {
  try {
    const app = getApps().length > 0 ? getApp() : initializeApp(stagingFirebaseConfig);
    return getFirestore(app);
  } catch (err) {
    console.warn('[LeadService] Staging Firebase init error:', err);
    return null;
  }
}

/**
 * Determine if current execution is strictly offline/local unit test without window.
 */
function isOfflineOrTest(): boolean {
  return typeof window === 'undefined';
}

/**
 * Dispatches consultation form inquiries.
 * 
 * In Staging:
 * - Direct client SDK submission into staging Firestore `coachingLeads`
 *   (bypassing Spark plan Cloud Functions restriction while populating tracker staging).
 * 
 * In Production:
 * - Calls same-origin HTTPS POST to `/api/submitLead` Cloud Function.
 */
export async function submitConsultationLead(
  lead: ConsultationLead & { honeypot?: string; renderedAt?: number }
): Promise<SubmitLeadResponse> {
  // If in Node/Unit-Test environment without window:
  if (isOfflineOrTest()) {
    const errors: Record<string, string> = {};
    if (!lead.fullName || lead.fullName.trim().length < 2) {
      errors.fullName = 'Full Name is required (minimum 2 characters).';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!lead.email || !emailRegex.test(lead.email.trim())) {
      errors.email = 'A valid email address is required.';
    }
    const phoneClean = (lead.phone || '').replace(/\D/g, '');
    if (!lead.phone || phoneClean.length < 7) {
      errors.phone = 'A valid phone number is required (minimum 7 digits).';
    }

    if (Object.keys(errors).length > 0) {
      return {
        success: false,
        error: 'Please correct the validation errors below.',
        errors
      };
    }

    return {
      success: true,
      isPreview: true,
      message: 'Form validation and plan selection verified (Interactive Preview).'
    };
  }

  const hostname = window.location.hostname;
  const isStagingEnv = hostname.includes('staging') || hostname === 'localhost' || hostname === '127.0.0.1';

  // If in Staging: write directly to staging Firestore coachingLeads
  if (isStagingEnv) {
    try {
      const db = getStagingDb();
      if (!db) {
        throw new Error('Firestore not initialized.');
      }

      const leadsRef = collection(db, 'coachingLeads');
      const docRef = await addDoc(leadsRef, {
        fullName: lead.fullName.trim(),
        email: lead.email.trim().toLowerCase(),
        phone: lead.phone.trim(),
        preferredPlanId: lead.preferredPlanId || '',
        fitnessGoal: lead.fitnessGoal,
        experienceLevel: lead.experienceLevel,
        marketCode: lead.marketCode || 'US',
        pricingVersionId: lead.pricingVersionId || '',
        message: lead.message?.trim() || '',
        status: 'new',
        source: 'marketing_staging_site',
        createdAt: new Date().toISOString(),
        serverCreatedAt: serverTimestamp(),
      });

      return {
        success: true,
        leadId: docRef.id,
        message: 'Consultation inquiry successfully received and queued.'
      };
    } catch (err: any) {
      console.error('[Consultation Staging Direct Ingestion Error]:', err);
      // Fallback to API rewrite if direct Firestore fails
    }
  }

  // Active Deployment: Same-origin HTTPS POST to Cloud Function rewrite
  const API_URL = import.meta.env?.VITE_LEADS_API_URL || '/api/submitLead';

  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(lead)
    });

    const data: SubmitLeadResponse = await response.json().catch(() => ({
      success: false,
      error: 'Invalid JSON response from server.'
    }));

    return data;
  } catch (err: any) {
    console.error('[Consultation API Error]:', err);
    return {
      success: false,
      error: 'Unable to reach the consultation intake server. Please check your network connection.'
    };
  }
}
